import React, { useEffect, useState } from "react";
import {
  ChevronRight,
  Clock,
  ExternalLink,
  ArrowRight,
  Video,
  FileText,
  Link2,
  Presentation,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../../../context/AuthContext";
import { getAllCourseAnnouncements } from "../../../../services/announcement.service";
import { useCourse } from "../../../../context/CourseContext";
import AssignmentsList from "../../../../components/dashboard/utils/AssignmentComponent/AssignmentList";
import { useUtilityContext } from "../../../../context/UtilityContext";
import {
  card,
  microLabel,
  cardTitle,
  bodySecondary,
  accentBarCard,
  motionStaggerChild,
  iconSize,
  dividerInner,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { getCardColor } from "../../../../utils/courseHelpers";

const StudentHome = ({ setSelectedOption, courseId }) => {
  const { user } = useAuth();
  const { courseData } = useCourse();
  const { setCurrentModuleIndex } = useUtilityContext();

  const upcomingEvents = [];
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Dynamic Data Calculations ---

  const totalLectures =
    courseData?.syllabus?.modules?.reduce(
      (acc, module) => acc + (module.lectureCount || 0),
      0
    ) || 0;

  const totalTopics =
    courseData?.weeklyPlan?.reduce(
      (acc, week) => acc + (week.topics?.length || 0),
      0
    ) || 0;

  const calculateCourseProgress = () => {
    if (!courseData?.semester?.startDate || !courseData?.semester?.endDate) {
      return 0;
    }
    const today = new Date();
    const startDate = new Date(courseData.semester.startDate);
    const endDate = new Date(courseData.semester.endDate);

    if (today >= endDate) return 100;
    if (today <= startDate) return 0;

    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsedDuration = today.getTime() - startDate.getTime();

    if (totalDuration <= 0) return 0;

    return Math.round((elapsedDuration / totalDuration) * 100);
  };

  const courseProgress = calculateCourseProgress();
  const completedTopics = Math.round((totalTopics * courseProgress) / 100);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await getAllCourseAnnouncements({ courseID: courseId });
      setAnnouncements(response.announcements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchAnnouncements();
    }
  }, [courseId]);

  // --- Stat cards config ---
  const statCards = [
    {
      label: "Total Students",
      value: courseData?.stats?.totalStudentsEnrolled,
      footer: "study buddies in this group",
    },
    {
      label: "Course Progress",
      value: "0%",
      footer: "course just started",
    },
    {
      label: "Total Lectures",
      value: totalLectures,
      footer: `Across ${courseData?.syllabus?.modules?.length || 0} modules`,
    },
    {
      label: "Upcoming",
      value: 0,
      footer: "events this week",
    },
  ];

  // Attendance card (conditional)
  const showAttendance =
    courseData?.stats?.studentView && courseData?.stats?.myAttendance;
  const attendancePct = showAttendance
    ? parseInt(courseData.stats.myAttendance.attendancePercentage)
    : 0;

  const getAttendanceColor = (pct) => {
    if (pct >= 75) return "text-accent1";
    if (pct >= 50) return "text-amber-600 dark:text-amber-500";
    return "text-red-600 dark:text-red-400";
  };


  return (
    <div className="mx-auto space-y-8 p-6">
      {/* Quick Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {statCards.map((stat, index) => {
          return (
            <motion.div
              key={stat.label}
              {...motionStaggerChild(index)}
              className={cn(card, "p-6 overflow-hidden")}
            >
              <div className={accentBarCard} />
              <div className="mt-4">
                <p className={microLabel}>{stat.label}</p>
                <h3 className="text-3xl font-black tracking-tight text-accent1 mt-1">
                  {stat.value}
                </h3>
              </div>
              <div className={cn("mt-4 flex items-center", bodySecondary)}>
                <span>{stat.footer}</span>
              </div>
            </motion.div>
          );
        })}

        {/* Attendance Card */}
        {showAttendance && (
          <motion.div
            {...motionStaggerChild(4)}
            className={cn(card, "p-6 overflow-hidden")}
          >
            <div className={accentBarCard} />
            <div className="mt-4">
              <p className={microLabel}>My Attendance</p>
              <h3
                className={cn(
                  "text-3xl font-black tracking-tight mt-1",
                  getAttendanceColor(attendancePct)
                )}
              >
                {courseData.stats.myAttendance.attendancePercentage}
              </h3>
            </div>
            <div className={cn("mt-4 flex items-center", bodySecondary)}>
              <span>
                {courseData.stats.myAttendance.sessionsAttended}/
                {courseData.stats.myAttendance.totalSessions} sessions attended
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column (2/3 width) */}
        <div className="lg:col-span-2 space-y-8">
          {/* Course Modules Section */}
          <motion.div
            {...motionStaggerChild(0, 0.3)}
            className={cn(card, "overflow-hidden")}
          >
            <div
              className={cn(
                "p-6 border-b flex justify-between items-center",
                dividerInner
              )}
            >
              <h2 className={cardTitle}>Course Modules</h2>
              <button
                className="text-accent1 hover:text-accent1/80 text-sm font-medium flex items-center transition-colors"
                onClick={() => setSelectedOption("E-Learning")}
              >
                View All Modules
                <ChevronRight className={cn(iconSize.md, "ml-1")} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {courseData?.syllabus?.modules?.map((module, index) => {
                const contentStats = [
                  { icon: Video, count: module.videoCount || 0, label: "Videos" },
                  { icon: FileText, count: module.pdfCount || 0, label: "PDFs" },
                  { icon: Presentation, count: module.pptCount || 0, label: "Slides" },
                  { icon: Link2, count: module.linkCount || 0, label: "Links" },
                ].filter((s) => s.count > 0);

                const cardColor = getCardColor(module.moduleTitle);

                return (
                  <motion.div
                    key={module._id}
                    {...motionStaggerChild(index, 0.4)}
                    whileHover={{ y: -2 }}
                    className={cn(
                      "group border cursor-pointer rounded-sm overflow-hidden transition-all duration-200 hover:shadow-md shadow-sm",
                      "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    )}
                    onClick={() => {
                      setSelectedOption("E-Learning");
                      setCurrentModuleIndex(index);
                    }}
                  >
                    {/* Color banner */}
                    <div
                      className="relative h-20 sm:h-24 w-full overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor} 60%, ${cardColor}cc 60%, ${cardColor}99 100%)`,
                      }}
                    >
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)`,
                        }}
                      />
                      <span className="absolute bottom-2.5 left-3 sm:left-4 text-white text-lg sm:text-xl font-bold tracking-wide drop-shadow-sm">
                        Module {module.moduleNumber || index + 1}
                      </span>
                    </div>

                    {/* Body */}
                    <div className="p-4">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-1">
                        {module.moduleTitle}
                      </h3>
                      {module.description && (
                        <p className={cn(bodySecondary, "mt-1.5 line-clamp-2")}>
                          {module.description}
                        </p>
                      )}
                    </div>

                    {/* Footer — content stats */}
                    <div className="px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between text-xs">
                        {contentStats.length > 0 ? (
                          <div className="flex gap-3">
                            {contentStats.map((s) => {
                              const SIcon = s.icon;
                              return (
                                <div
                                  key={s.label}
                                  className="flex items-center gap-1 text-gray-500 dark:text-gray-400"
                                >
                                  <SIcon className={iconSize.xs} />
                                  <span className="font-medium">
                                    {s.count}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 italic">
                            No content yet
                          </span>
                        )}
                        <ArrowRight className={cn(iconSize.md, "text-gray-400 dark:text-gray-500 group-hover:text-accent1 transition-colors")} />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Assignments Section */}
          <AssignmentsList
            setSelectedOption={setSelectedOption}
            courseId={courseId}
          />
        </div>

        {/* Right Column (1/3 width) */}
        <div className="space-y-8">
          {/* Announcements Section */}
          <motion.div
            {...motionStaggerChild(1, 0.3)}
            className={cn(card, "overflow-hidden")}
          >
            <div
              className={cn(
                "p-6 border-b flex justify-between items-center",
                dividerInner
              )}
            >
              <h2 className={cardTitle}>Announcements</h2>
            </div>

            <div className={cn("divide-y", dividerInner)}>
              {loading ? (
                [1, 2].map((i) => (
                  <div key={i} className="p-6 space-y-3 animate-pulse">
                    <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                    <div className="h-3 w-1/3 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                    <div className="h-40 w-full bg-gray-200 dark:bg-gray-700 rounded-sm" />
                    <div className="h-3 w-full bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                    <div className="h-3 w-4/5 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                  </div>
                ))
              ) : announcements.length === 0 ? (
                <div className={cn("p-6 text-center", bodySecondary)}>
                  No announcements available.
                </div>
              ) : (
                announcements.map((announcement, i) => (
                  <motion.div
                    key={announcement._id}
                    {...motionStaggerChild(i, 0.1)}
                    className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {announcement.title}
                    </h3>
                    <div className={cn("mt-1", bodySecondary)}>
                      {new Date(announcement.publishDate).toLocaleDateString()}{" "}
                      {new Date(announcement.publishDate).toLocaleTimeString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </div>
                    <img
                      src={announcement.image?.imageUrl || "/announcement.png"}
                      alt={announcement.title}
                      className="mt-3 w-full h-40 object-cover rounded-sm border border-gray-200 dark:border-gray-700"
                    />
                    <p className={cn("mt-3 line-clamp-3 text-sm", bodySecondary)}>
                      {announcement.content}
                    </p>
                  </motion.div>
                ))
              )}
            </div>

            <div
              className={cn("p-4 border-t", dividerInner)}
            >
              <button
                className="w-full py-2 text-accent1 hover:text-accent1/80 text-sm font-medium flex items-center justify-center transition-colors"
                onClick={() => {
                  setSelectedOption("Announcements");
                }}
              >
                View All Announcements
                <ExternalLink className={cn(iconSize.md, "ml-1")} />
              </button>
            </div>
          </motion.div>

          {/* Calendar Section */}
          <motion.div
            {...motionStaggerChild(2, 0.3)}
            className={cn(card, "overflow-hidden")}
          >
            <div
              className={cn(
                "p-6 border-b flex justify-between items-center",
                dividerInner
              )}
            >
              <h2 className={cardTitle}>Upcoming Events</h2>
              <div className={cn(bodySecondary, "font-medium")}>
                {new Date().toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </div>

            <div className={cn("divide-y", dividerInner)}>
              {upcomingEvents.map((event, i) => (
                <motion.div
                  key={event.id}
                  {...motionStaggerChild(i, 0.1)}
                  className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-accent1/10 rounded-sm flex flex-col items-center justify-center">
                      <span className="text-accent1 font-bold text-lg">
                        {event.date.split(" ")[1].replace(",", "")}
                      </span>
                      <span className={bodySecondary}>
                        {event.date.split(" ")[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white">
                        {event.title}
                      </h3>
                      <div
                        className={cn(
                          "flex items-center mt-1",
                          bodySecondary
                        )}
                      >
                        <Clock className={cn(iconSize.xs, "mr-1")} />
                        <span>{event.time}</span>
                      </div>
                      <div className={cn("mt-1", bodySecondary)}>
                        {event.location}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {upcomingEvents.length === 0 && (
              <div className="p-8 text-center">
                <Clock className={cn(iconSize.empty, "text-gray-300 dark:text-gray-600 mx-auto mb-3")} />
                <p className={bodySecondary}>No upcoming events</p>
              </div>
            )}

          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StudentHome;
